<script setup lang="ts">
import type { GraphqlDatatableColumn } from '~/components/GqlDatatable.vue';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { computed, ref } from 'vue';
import { CompromissoForm } from '~/lib/fw';
import { useAuthenticationStore } from '~/stores/authenticationStore';

interface Props {
  pastaCodigos?: string[];
  publicacaoNumeroProcessoCodigos?: string[];
  compromisso?: any;
  fluxo?: any;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const tipoCompromissoPubGql = gql`
  query tipoCompromissoPubQuery {
    data: tipoCompromissoPub {
      label
      value
    }
  }
`;

const usuariosGql = gql`
  query usuariosSelect(
    $where: UsuarioModelFilterInput
    $order: [UsuarioModelSortInput!]
  ) {
    data: usuariosAll2(where: $where, order: $order) {
      value: codigo
      label: nome
    }
  }
`;

const mostrarHistoricoSolicitacoes = ref<string>('h');
const colunas: GraphqlDatatableColumn[] = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
];

const authenticationStore = useAuthenticationStore();
const buttonsDisabled = computed(() => {
  if (!props.compromisso) {
    return false;
  }
  return authenticationStore.user!.id !== props.compromisso.responsavelCodigo;
});

const showResponsavel = computed(() => {
  if (
    props.publicacaoNumeroProcessoCodigos &&
    props.publicacaoNumeroProcessoCodigos.length >= 1
  ) {
    return true;
  }
  return false;
});

const showDescricao = computed(() => {
  if (props.pastaCodigos && props.pastaCodigos.length >= 1) {
    return true;
  }
  return false;
});

function disabledDate(current: any) {
  return (
    current &&
    (current.day() === 0 ||
      current.day() === 6 ||
      current < dayjs().endOf('day'))
  );
}
</script>

<template>
  <CompromissoForm
    action="compromisso-pub/criar-compromisso-pub"
    :pasta-codigos="props.pastaCodigos ?? []"
    :publicacao-numero-processo-codigos="
      props.publicacaoNumeroProcessoCodigos ?? []
    "
    :compromisso-codigos="props.compromisso ? [props.compromisso.codigo] : []"
    :wait-for-result="true"
    :show-upload-files="true"
    :submit-button-disabled="buttonsDisabled"
    @close="() => emit('close')"
  >
    <template #form="{ model }">
      <a-form-item
        name="iraClassificar"
        label="Vai classificar esta publicação?"
        :rules="[
          {
            message: 'Campo obrigatório',
            required: true,
            type: 'boolean',
          },
        ]"
      >
        <a-radio-group v-model:value="model.iraClassificar" button-style="solid">
          <a-radio-button :value="true">Sim</a-radio-button>
          <a-radio-button :value="false">Não</a-radio-button>
        </a-radio-group>
      </a-form-item>

      <template v-if="model.iraClassificar === true">
        <a-collapse
          v-if="props.pastaCodigos?.length === 1"
          v-model:active-key="mostrarHistoricoSolicitacoes"
          style="margin-bottom: 24px"
        >
          <a-collapse-panel key="h" header="HISTÓRICO DE SOLICITAÇÕES">
            <CompromissoGqlDatatable
              :columns="colunas"
              :show-dates-filter="false"
              :show-reload-button="false"
              :show-status-search-bar="false"
              :show-filter="false"
              :variables="{
                order: [{ dataInicio: 'DESC' }],
                where: {
                  tipo: {
                    eq: 59,
                  },
                  fluxo: {
                    pastaCodigo: {
                      in: [props.pastaCodigos[0]],
                    },
                  },
                },
              }"
            />
          </a-collapse-panel>
        </a-collapse>

        <a-form-item
          name="tipo"
          label="Tipo"
          :rules="[
            {
              message: 'Tipo do compromisso é obrigatório',
              required: true,
              trigger: 'change',
              type: 'object',
            },
          ]"
        >
          <GqlSelect
            v-model:value="model.tipo"
            :query="tipoCompromissoPubGql"
            placeholder="Selecione um tipo"
          />
        </a-form-item>

        <a-form-item
          v-if="showResponsavel"
          name="responsavel"
          label="Responsável"
          :rules="[
            {
              message: 'Responsável é obrigatório',
              required: true,
              trigger: 'change',
              type: 'object',
            },
          ]"
        >
          <GqlSelect
            v-model:value="model.responsavel"
            :query="usuariosGql"
            placeholder="Selecione o responsável"
            :allow-clear="true"
            :variables="{
              where: {
                ativo: {
                  eq: true,
                },
              },
              order: [
                {
                  nome: 'ASC',
                },
              ],
            }"
          />
        </a-form-item>

        <a-form-item
          v-if="showDescricao"
          label="Descrição"
          name="descricao"
          :rules="[
            {
              message: 'Descrição é obrigatório',
              required: true,
            },
          ]"
        >
          <a-textarea v-model:value="model.descricao" :rows="4" />
        </a-form-item>

        <a-form-item
          label="Prazo"
          name="dataPrazo"
          :rules="[{ message: 'Prazo é obrigatório', required: true }]"
        >
          <a-date-picker
            v-model:value="model.dataPrazo"
            format="DD/MM/YYYY HH:mm"
            value-format="DD/MM/YYYY HH:mm"
            placeholder="Selecione uma data"
            style="width: 100%"
            :disabled-date="disabledDate"
            :show-time="{ defaultValue: dayjs('17:00:00', 'HH:mm:ss') }"
          />
        </a-form-item>
      </template>

      <a-form-item
        v-if="model.iraClassificar === false"
        name="justificativaNaoClassificacao"
        label="Justificativa"
        :rules="[
          {
            message: 'Justificativa é obrigatória',
            required: true,
          },
        ]"
      >
        <a-textarea
          v-model:value="model.justificativaNaoClassificacao"
          :rows="4"
          placeholder="Descreva por que a publicação não será classificada"
        />
      </a-form-item>
    </template>
  </CompromissoForm>
</template>
